import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import cleanText from 'utils/cleanText';

import operationTypes from 'constants/operationTypes';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';
import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { useGetLastOperationDate } from 'modules/transactions/hooks/useGetLastOperationDate';
import { addOperation } from 'modules/transactions/redux/operationActions';

import { getNewOperationDate } from 'utils/getNewOperationDate';

import { cleanValue } from 'constants/formats';
import { DividendsDistributionModalContextType } from '../types/DividendsDistributionModalContextType';
import { dividendsDistributionModalInitValue } from '../constants/dividendsDistributionModalInitValue';
import { useGetDividends } from '../hooks/useGetDividends';

const DividendsDistributionModalContext =
  createContext<DividendsDistributionModalContextType>(
    dividendsDistributionModalInitValue
  );

export const DividendsDistributionModalProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const { lastOperationDate, formattedLastOperationDate } =
    useGetLastOperationDate(actualSociety?._id).data;

  const [scriptureView, setScriptureView] = useState<boolean>(true);
  const [date, setDate] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [file, setFile] = useState<File | undefined>();
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [hasRetention, setHasRetention] = useState<boolean>(false);
  const [retentionPercentage, setRetentionPercentage] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    data: { dividends },
    isLoading: isDividendsLoading,
  } = useGetDividends({
    societyId: actualSociety?._id,
    societyActualSharesCount: actualSociety?.sharesCount?.actual || 0,
    totalAmount,
    page: currentPage,
    retentionPercentage: hasRetention ? retentionPercentage : 0,
  });

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeDate = (event: ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
  };

  const handleChangeComments = (value: string) => {
    setComments(value);
  };

  const handleChangeFile = (file: File) => {
    setFile(file);
  };

  const handleChangeTotalAmount = (event: ChangeEvent<HTMLInputElement>) => {
    const value = cleanValue(event?.target?.value);
    const totalAmount = Number(value);

    setTotalAmount(totalAmount);
  };

  const handleChangeHasRetention = () => {
    setHasRetention((prev) => !prev);
  };

  const handleChangeRetentionPercentage = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const value = cleanValue(event?.target?.value);
    const percentage = Number(value);

    setRetentionPercentage(percentage);
  };

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  const handleChangeRowsPerPage = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage);
  };

  const handleSaveDividends = async () => {
    if (scriptureView) {
      setScriptureView(false);
    } else {
      try {
        setIsLoading(true);

        let uploadedDocument = null;

        if (file) {
          const newDocument = {
            file,
            date: new Date(date),
            size: file.size,
            name: file.name,
            author: user['_id'],
            society: actualSociety['_id'],
            fileType: fileTypes[file.type as keyof typeof fileTypes],
            category: documentTypes.SCRIPTURES,
            description: t('DocumentLinkedToOperation'),
          };
          uploadedDocument = await store.dispatch(addDocument(newDocument));
        }

        const newOperationDate = getNewOperationDate(lastOperationDate, date);

        const dividendsData = {
          movements: [],
          transactions: [],
          dividend: { totalAmount, retentionPercentage },
          date: newOperationDate,
          nominalValue: actualSociety.nominalValue,
          society: actualSociety['_id'],
          user: user['_id'],
          documents: uploadedDocument?.['_id'] ? [uploadedDocument['_id']] : [],
          comments: cleanText(comments),
          operationType: operationTypes.DIVIDENDS_DISTRIBUTION,
        };

        await store.dispatch(addOperation(dividendsData));
        setIsLoading(false);
        successSwal(t('DividendsSuccessfullyCompleted'));
      } catch (error) {
        errorSwal(t('ErrorWhenPerformingDividends'));
      } finally {
        handleCloseModal();
      }
    }
  };

  return (
    <DividendsDistributionModalContext.Provider
      value={{
        data: {
          date,
          comments,
          file,
          scriptureView,
          lastOperationDate: formattedLastOperationDate,
          totalAmount,
          hasRetention,
          retentionPercentage,
          dividends,
          currentPage,
          rowsPerPage,
          isLoading,
          isDividendsLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeDate,
          handleChangeComments,
          handleChangeFile,
          handleChangeTotalAmount,
          handleChangeHasRetention,
          handleChangeRetentionPercentage,
          handleChangePage,
          handleChangeRowsPerPage,
          handleSaveDividends,
        },
      }}
    >
      {children}
    </DividendsDistributionModalContext.Provider>
  );
};

export const useDividendsDistributionModal = () => {
  const context = useContext(DividendsDistributionModalContext);
  if (!context) {
    throw new Error(
      'useDividendsDistribution must be used within a DividensDistributionModalProvider'
    );
  }
  return context;
};
